/** @jsxImportSource @emotion/react */
import { createStyles, UtilityButton, Modal } from '@quickbit/qb-design-system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { IllustrationWithText } from 'components';
import { useOrderDetails } from 'hooks';
import { TransactionDetails } from './components';

export const Refunded = () => {
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const { order } = useOrderDetails();
  const [seconds, setSeconds] = useState(5);
  const [showReceipt, setShowReceipt] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const refundRedirectUrl = order?.order?.refundRedirectUrl;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const failureRedirectUrl = order?.order?.failureRedirectUrl;

  const transactionId =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    order?.order?.refund?.refundTransactionId ||
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    order?.order?.manualRefund?.refundTransactionId;

  const redirectBackToMerchant = useCallback(
    (restart?: boolean) => {
      if (restart) setSeconds(5);
      if (refundRedirectUrl) {
        intervalRef.current = setTimeout(() => {
          sessionStorage.clear();
          window.location.replace(refundRedirectUrl);
        }, 5000);
      } else if (failureRedirectUrl) {
        intervalRef.current = setTimeout(() => {
          sessionStorage.clear();
          window.location.replace(failureRedirectUrl);
        }, 5000);
      }
    },
    [refundRedirectUrl, failureRedirectUrl]
  );

  useEffect(() => {
    redirectBackToMerchant();
    return () => clearTimeout(intervalRef.current as NodeJS.Timeout);
  }, [redirectBackToMerchant]);

  useEffect(() => {
    const timeout = setInterval(() => {
      if (seconds === 0) {
        clearInterval(timeout);
      } else {
        setSeconds((prev) => prev - 1);
      }
    }, 1000);
    return () => clearInterval(timeout);
  }, [seconds]);

  const handleBackToMerchant = useCallback(() => {
    sessionStorage.clear();
    if (refundRedirectUrl) {
      window.location.replace(refundRedirectUrl);
    } else if (failureRedirectUrl) {
      window.location.replace(failureRedirectUrl);
    }
  }, [refundRedirectUrl, failureRedirectUrl]);

  return (
    <>
      <div css={styles.main}>
        <IllustrationWithText
          illustration="transferSuccess"
          heading={formatMessage({
            id: 'page.refunded.heading',
          })}
          subheading={
            failureRedirectUrl || refundRedirectUrl
              ? formatMessage({ id: 'redirect.backToMerchant' }, { seconds })
              : formatMessage({ id: 'page.refunded.subHeading' })
          }
        />
        {transactionId && (
          <UtilityButton
            text="View Details"
            onClick={() => {
              clearInterval(intervalRef.current as NodeJS.Timeout);
              setShowReceipt(true);
            }}
            variant="text"
          />
        )}
      </div>
      {showReceipt && transactionId && (
        <Modal
          closeModal={() => {
            redirectBackToMerchant(true);
            setShowReceipt(false);
          }}
          title={formatMessage({ id: 'page.refunded.receipt' })}
        >
          <TransactionDetails
            transactionId={transactionId}
            redirectBackAction={
              refundRedirectUrl || failureRedirectUrl
                ? handleBackToMerchant
                : undefined
            }
          />
        </Modal>
      )}
    </>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  main: {
    display: 'flex',
    justifyContent: 'space-around',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    gap: spacing.xxs,
  },
}));
